<template>
  <div id="app" :class="{ dark: hasDarkMode }">
    <ion-app>
      <app-side-menu v-if="isMobile && (user || skipSignUp)" />
      <web-side-menu v-if="!isMobile && user" />
      <ion-vue-router id="content" />

      <ion-alert-controller></ion-alert-controller>
      <ion-menu-controller></ion-menu-controller>
      <ion-toast-controller></ion-toast-controller>
    </ion-app>
  </div>
</template>

<script>
import AppSideMenu from "@/components/App/SideMenu";
import WebSideMenu from "@/components/Web/SideMenu";
import PlatformService from "@/services/Platform";
import { mapGetters } from "vuex";
import { Plugins } from "@capacitor/core";
const { SplashScreen } = Plugins;

export default {
  mounted() {
    this.$store.dispatch("auth/initializeUser");
  },

  created() {
    SplashScreen.hide();
  },

  computed: {
    isMobile: () => PlatformService.isMobile(),
    ...mapGetters({
      user: "auth/user",
      skipSignUp: "auth/skipSignUp",
      hasDarkMode: "darkMode"
    })
  },

  components: {
    "app-side-menu": AppSideMenu,
    "web-side-menu": WebSideMenu
  }
};
</script>

<style lang="scss">
@font-face {
  font-family: SourceSansProNormal;
  src: url("./assets/fonts/SourceSansPro-Regular.ttf");
}
@font-face {
  font-family: PlayfairDisplayBoldItalic;
  src: url("./assets/fonts/PlayfairDisplay-BoldItalic.ttf");
}
@font-face {
  font-family: RalewayMedium;
  src: url("./assets/fonts/Raleway-Medium.ttf");
}

/***************************************************************************************************
 * Dark Mode Colors suggested by Ionic
 **************************************************************************************************/

#app.dark {
  --ion-color-primary: #428cff;
  --ion-color-primary-rgb: 66, 140, 255;
  --ion-color-primary-contrast: #ffffff;
  --ion-color-primary-contrast-rgb: 255, 255, 255;
  --ion-color-primary-shade: #3a7be0;
  --ion-color-primary-tint: #5598ff;

  --ion-color-secondary: #50c8ff;
  --ion-color-secondary-rgb: 80, 200, 255;
  --ion-color-secondary-contrast: #ffffff;
  --ion-color-secondary-contrast-rgb: 255, 255, 255;
  --ion-color-secondary-shade: #46b0e0;
  --ion-color-secondary-tint: #62ceff;

  --ion-color-tertiary: #6a64ff;
  --ion-color-tertiary-rgb: 106, 100, 255;
  --ion-color-tertiary-contrast: #ffffff;
  --ion-color-tertiary-contrast-rgb: 255, 255, 255;
  --ion-color-tertiary-shade: #5d58e0;
  --ion-color-tertiary-tint: #7974ff;

  --ion-color-success: #2fdf75;
  --ion-color-success-rgb: 47, 223, 117;
  --ion-color-success-contrast: #000000;
  --ion-color-success-contrast-rgb: 0, 0, 0;
  --ion-color-success-shade: #29c467;
  --ion-color-success-tint: #44e283;

  --ion-color-warning: #ffd534;
  --ion-color-warning-rgb: 255, 213, 52;
  --ion-color-warning-contrast: #000000;
  --ion-color-warning-contrast-rgb: 0, 0, 0;
  --ion-color-warning-shade: #e0bb2e;
  --ion-color-warning-tint: #ffd948;

  --ion-color-danger: #ff4961;
  --ion-color-danger-rgb: 255, 73, 97;
  --ion-color-danger-contrast: #ffffff;
  --ion-color-danger-contrast-rgb: 255, 255, 255;
  --ion-color-danger-shade: #e04055;
  --ion-color-danger-tint: #ff5b71;

  --ion-color-dark: #f4f5f8;
  --ion-color-dark-rgb: 244, 245, 248;
  --ion-color-dark-contrast: #000000;
  --ion-color-dark-contrast-rgb: 0, 0, 0;
  --ion-color-dark-shade: #d7d8da;
  --ion-color-dark-tint: #f5f6f9;

  --ion-color-medium: #989aa2;
  --ion-color-medium-rgb: 152, 154, 162;
  --ion-color-medium-contrast: #000000;
  --ion-color-medium-contrast-rgb: 0, 0, 0;
  --ion-color-medium-shade: #86888f;
  --ion-color-medium-tint: #a2a4ab;

  --ion-color-light: #222428;
  --ion-color-light-rgb: 34, 36, 40;
  --ion-color-light-contrast: #ffffff;
  --ion-color-light-contrast-rgb: 255, 255, 255;
  --ion-color-light-shade: #1e2023;
  --ion-color-light-tint: #383a3e;
}

.ios #app.dark {
  --ion-background-color: #000000;
  --ion-background-color-rgb: 0, 0, 0;

  --ion-text-color: #ffffff;
  --ion-text-color-rgb: 255, 255, 255;

  --ion-color-step-50: #0d0d0d;
  --ion-color-step-100: #1a1a1a;
  --ion-color-step-150: #262626;
  --ion-color-step-200: #333333;
  --ion-color-step-250: #404040;
  --ion-color-step-300: #4d4d4d;
  --ion-color-step-350: #595959;
  --ion-color-step-400: #666666;
  --ion-color-step-450: #737373;
  --ion-color-step-500: #808080;
  --ion-color-step-550: #8c8c8c;
  --ion-color-step-600: #999999;
  --ion-color-step-650: #a6a6a6;
  --ion-color-step-700: #b3b3b3;
  --ion-color-step-750: #bfbfbf;
  --ion-color-step-800: #cccccc;
  --ion-color-step-850: #d9d9d9;
  --ion-color-step-900: #e6e6e6;
  --ion-color-step-950: #f2f2f2;

  --ion-toolbar-background: #0d0d0d;

  --ion-item-background: #1c1c1c;
  --ion-item-background-activated: #313131;
}

.md #app.dark {
  --ion-background-color: #121212;
  --ion-background-color-rgb: 18, 18, 18;

  --ion-text-color: #ffffff;
  --ion-text-color-rgb: 255, 255, 255;

  --ion-border-color: #222222;

  --ion-color-step-50: #1e1e1e;
  --ion-color-step-100: #2a2a2a;
  --ion-color-step-150: #363636;
  --ion-color-step-200: #414141;
  --ion-color-step-250: #4d4d4d;
  --ion-color-step-300: #595959;
  --ion-color-step-350: #656565;
  --ion-color-step-400: #717171;
  --ion-color-step-450: #7d7d7d;
  --ion-color-step-500: #898989;
  --ion-color-step-550: #949494;
  --ion-color-step-600: #a0a0a0;
  --ion-color-step-650: #acacac;
  --ion-color-step-700: #b8b8b8;
  --ion-color-step-750: #c4c4c4;
  --ion-color-step-800: #d0d0d0;
  --ion-color-step-850: #dbdbdb;
  --ion-color-step-900: #e7e7e7;
  --ion-color-step-950: #f3f3f3;

  --ion-item-background: #1a1b1e;
}

/***************************************************************************************************
 * All Site Setup
 **************************************************************************************************/

:root {
  --ion-font-family: SourceSansProNormal, sans-serif;
}

:root,
:root #app.dark {
  --ion-color-primary: #706f70;
  --ion-color-primary-rgb: 112, 111, 112;
  --ion-color-primary-contrast: #ffffff;
  --ion-color-primary-contrast-rgb: 255, 255, 255;
  --ion-color-primary-shade: #636263;
  --ion-color-primary-tint: #7e7d7e;
}

ion-header ion-toolbar,
ion-footer ion-toolbar {
  --background: #000000;
  --color: #ffffff;
  font-family: PlayfairDisplayBoldItalic;
}
ion-footer ion-toolbar.available-order {
  --background: #3880ff;
  font-family: SourceSansProNormal, sans-serif;
}

ion-header.web {
  border-top: 2px solid var(--ion-color-primary);
}

// this just doesn't normally look good with background color
ion-card-header + .card-content-md {
  padding-top: 16px;
}

ion-toolbar ion-select,
ion-toolbar ion-datetime,
ion-toolbar ion-searchbar .searchbar-input-container {
  background: #ffffff;
  color: #000000;
}
ion-select.next-to-search.md {
  margin-top: 3px;
}

.ios ion-item.item-lines-none {
  --min-height: auto;
}

.md ion-item.item-lines-none {
  --min-height: 40px;
}

.ios ion-item-divider {
  --background: transparent;
}

#card-element {
  min-height: 24px;
  width: 100%;
  margin-top: 15px;
  margin-bottom: 4px;
}

/***************************************************************************************************
 * Generic Helpers
 **************************************************************************************************/
hr.divider {
  background-color: #000000;
  width: 3.4rem;
}
.w-100 {
  width: 100%;
}
.background-color {
  background: var(--ion-color-base);
}
.text-color {
  color: var(--ion-color-base);
}
.ion-no-padding-bottom {
  padding-bottom: 0;
}
.ion-standard-font {
  font-family: var(--ion-font-family);
}
.ion-padding-top-sm {
  padding-top: 10px;
}
.ion-no-margin-top {
  margin-top: 0;
}

/***************************************************************************************************
 * Unique Items
 **************************************************************************************************/
#service-charge:before {
  content: "$";
}

.powered-by-stripe {
  height: 32px;
}

.order-table {
  .header {
    background-color: var(--ion-color-primary-tint);
    color: var(--ion-color-primary-contrast);
  }

  ion-row:not(.header) {
    &:hover {
      background-color: rgba(var(--ion-color-primary-rgb), 0.1);
    }
    ion-col {
      border: 1px solid rgba(var(--ion-color-primary-rgb), 0.25);
    }
  }
  ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
  }
}

.ion-stop-ellipsis {
  overflow: visible;
  text-overflow: unset;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}
</style>
